/**
 * TermsAndPrivacyPage Component
 *
 * Displays the merged Terms of Service and Privacy Policy legal document.
 * Reached from User Settings → Legal, signed-in users only (/<tenant>/settings/terms).
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLanguage } from '@hooks/useLanguage';
import './LegalPage.css';

export default function TermsAndPrivacyPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button className="legal-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeftOutlined className="rtl-flip" />
          {t('legal.backButton')}
        </button>

        <div className="legal-content">
          <h1 className="legal-title">{t('legal.termsAndPrivacy.title')}</h1>
          <p className="legal-updated">
            {t('legal.lastUpdated')}: January 20, 2026
          </p>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.acceptance.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.acceptance.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.description.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.description.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.accounts.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.accounts.content')}</p>
            <ul>
              <li>{t('legal.termsAndPrivacy.sections.accounts.items.accurate')}</li>
              <li>{t('legal.termsAndPrivacy.sections.accounts.items.secure')}</li>
              <li>{t('legal.termsAndPrivacy.sections.accounts.items.notify')}</li>
              <li>{t('legal.termsAndPrivacy.sections.accounts.items.responsible')}</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.acceptable.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.acceptable.content')}</p>
            <ul>
              <li>{t('legal.termsAndPrivacy.sections.acceptable.items.laws')}</li>
              <li>{t('legal.termsAndPrivacy.sections.acceptable.items.rights')}</li>
              <li>{t('legal.termsAndPrivacy.sections.acceptable.items.interference')}</li>
              <li>{t('legal.termsAndPrivacy.sections.acceptable.items.malicious')}</li>
              <li>{t('legal.termsAndPrivacy.sections.acceptable.items.unauthorized')}</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.intellectual.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.intellectual.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.userContent.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.userContent.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.dataCollection.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.dataCollection.content')}</p>
            <ul>
              <li>{t('legal.termsAndPrivacy.sections.dataCollection.items.account')}</li>
              <li>{t('legal.termsAndPrivacy.sections.dataCollection.items.content')}</li>
              <li>{t('legal.termsAndPrivacy.sections.dataCollection.items.usage')}</li>
              <li>{t('legal.termsAndPrivacy.sections.dataCollection.items.device')}</li>
              <li>{t('legal.termsAndPrivacy.sections.dataCollection.items.cookies')}</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.dataUse.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.dataUse.content')}</p>
            <ul>
              <li>{t('legal.termsAndPrivacy.sections.dataUse.items.provide')}</li>
              <li>{t('legal.termsAndPrivacy.sections.dataUse.items.develop')}</li>
              <li>{t('legal.termsAndPrivacy.sections.dataUse.items.communicate')}</li>
              <li>{t('legal.termsAndPrivacy.sections.dataUse.items.security')}</li>
              <li>{t('legal.termsAndPrivacy.sections.dataUse.items.legal')}</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.dataSharing.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.dataSharing.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.security.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.security.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.retention.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.retention.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.userRights.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.userRights.content')}</p>
            <ul>
              <li>{t('legal.termsAndPrivacy.sections.userRights.items.access')}</li>
              <li>{t('legal.termsAndPrivacy.sections.userRights.items.correction')}</li>
              <li>{t('legal.termsAndPrivacy.sections.userRights.items.deletion')}</li>
              <li>{t('legal.termsAndPrivacy.sections.userRights.items.portability')}</li>
              <li>{t('legal.termsAndPrivacy.sections.userRights.items.objection')}</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.disclaimer.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.disclaimer.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.limitation.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.limitation.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.termination.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.termination.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.changes.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.changes.content')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.termsAndPrivacy.sections.contact.title')}</h2>
            <p>{t('legal.termsAndPrivacy.sections.contact.content')}</p>
          </section>
        </div>
      </div>
    </div>
  );
}
